<template>
  <div>
    <v-dialog v-model="isOpen" persistent max-width="1200">
      <v-card class="align-center">
        <v-card-title>
          <div class="d-flex align-center justify-space-between w-100">
            <h1 class="py-5 text-left">Detalhes da Fatura</h1>
            <v-btn icon @click="close">
              <v-icon> mdi-close </v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text v-if="isLoading" class="d-flex align-center justify-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-card-text>

        <v-card-text v-else>
          <v-row class="mt-1 invoice-info">
            <v-col cols="12" md="4">
              <p class="mb-2">Id da Fatura</p>
              <span>{{ invoiceDetails.numberInvoiceId }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">Id da Fatura Externa</p>
              <span>{{ invoiceDetails.externalInvoiceNumber ? invoiceDetails.externalInvoiceNumber : '-' }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">Data de criação</p>
              <span>{{ transformDate(invoiceDetails.invoiceDate) }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">Nome do Embarcador</p>
              <span>{{ invoiceDetails.senderName }}</span>
            </v-col>

            <v-col cols="6" md="4">
              <p class="mb-2">Previsão de Recebimento</p>
              <span>{{ transformDate(invoiceDetails.receiptForecast) }}</span>
            </v-col>

            <v-col cols="6" md="4">
              <p class="mb-2">Data de Recebimento</p>
              <span>{{ invoiceDetails.paymentReceived ? transformDate(invoiceDetails.paymentReceived) : '-' }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">N° Recebimento da Fatura</p>
              <span>{{ invoiceDetails.receiptNumber ? invoiceDetails.receiptNumber : '-' }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">Observação</p>
              <span>{{ invoiceDetails.observation ? invoiceDetails.observation : 'Sem observações' }}</span>
            </v-col>

            <v-col cols="12" md="4">
              <p class="mb-2">Motivo da Rejeição pelo Embarcador</p>
              <span>{{ invoiceDetails.approvalObservation ? invoiceDetails.approvalObservation : '-' }}</span>
            </v-col>
          </v-row>

          <h1 class="mt-6 text-left">Descrição das CTEs</h1>
          <v-row>
            <v-col cols="12">
              <div class="table mt-10 px-0">
                <v-data-table
                  :headers="columnsCtes"
                  :items="invoiceDetails.descriptionCtes"
                  :items-per-page="paginationCtes.pageSize"
                  :page.sync="paginationCtes.pageNumber"
                  @update:items-per-page="changeItemsPerPage"
                  @page-count="paginationCtes.totalPages = $event"
                  :footer-props="{
                    'items-per-page-options': [5, 10, 20, 30, 50],
                    itemsPerPageText: 'Itens por página'
                  }"
                >
                  <template v-slot:[`item.price`]="{ item }">
                    {{ transformPrice(item.price) }}
                  </template>
                  <template v-slot:[`item.observation`]="{ item }">
                    <TruncateDataWithTooltip
                      :data="item.observation"
                      :lengthDataToTruncate="10"
                    ></TruncateDataWithTooltip>
                  </template>
                </v-data-table>
                <v-pagination
                  v-model="paginationCtes.pageNumber"
                  :length="totalPagesTable"
                  :total-visible="7"
                  @input="onPageChange"
                ></v-pagination>
              </div>
            </v-col>
          </v-row>

          <h1 class="mt-6 text-left">Descrição de Adicionais</h1>
          <v-row>
            <v-col cols="12">
              <div class="table mt-10 mb-5 px-0">
                <v-data-table
                  :headers="columnsAditionals"
                  :items="invoiceDetails.descriptionAdditionals"
                  :items-per-page="invoiceDetails.descriptionAdditionals.length"
                  fixed-header
                  :height="aditionalsTableHeight"
                  hide-default-footer
                  no-data-text="Fatura sem adicionais"
                >
                  <template v-slot:[`item.additionalDate`]="{ item }">
                    {{ transformDate(item.additionalDate) }}
                  </template>

                  <template v-slot:[`item.price`]="{ item }">
                    {{ transformPrice(item.price) }}
                  </template>

                  <template v-slot:[`item.isDiscount`]="{ item }">
                    {{ transformDiscount(item.isDiscount) }}
                  </template>

                  <template v-slot:[`item.complementaryAccessKeyCte`]="{ item }">
                    <TruncateDataWithTooltip
                      :data="item.complementaryAccessKeyCte"
                      :lengthDataToTruncate="20"
                    ></TruncateDataWithTooltip>
                  </template>

                  <template v-slot:[`item.observation`]="{ item }">
                    <TruncateDataWithTooltip
                      :data="item.observation"
                      :lengthDataToTruncate="40"
                    ></TruncateDataWithTooltip>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="d-flex justify-space-around mx-12">
              <div class="d-flex money-cards">
                <div class="circle">
                  <v-icon class="pa-3" large color="white"> mdi-cash-minus </v-icon>
                </div>
                <span class="d-flex px-2 flex-column justify-center">
                  TOTAL DE DÉBITO
                  <strong>{{ transformPrice(receiptDiscount) }}</strong>
                </span>
              </div>

              <div class="d-flex money-cards">
                <div class="circle">
                  <v-icon class="pa-3" large color="white"> mdi-cash-plus </v-icon>
                </div>
                <span class="d-flex px-2 flex-column justify-center">
                  TOTAL DE CRÉDITO
                  <strong> {{ transformPrice(receiptValue) }} </strong>
                </span>
              </div>

              <div class="d-flex money-cards">
                <div class="circle">
                  <v-icon class="pa-3" large color="white"> mdi-cash-register </v-icon>
                </div>
                <span class="d-flex px-2 flex-column justify-center">
                  TOTAL DA FATURA
                  <strong>
                    {{ transformPrice(receiptValue - receiptDiscount) }}
                  </strong>
                </span>
              </div>
            </v-col>
          </v-row>

          <v-card-actions class="d-flex justify-center py-5">
            <v-btn color="error" class="mx-2 px-5" large @click="close"> Cancelar </v-btn>
            <v-btn color="success" class="mx-2 px-5" large @click="openInvoiceEdit" v-if="!isShipper"> Editar </v-btn>
            <v-btn color="indigo" class="mx-2 px-5" large outlined @click="getPDF()"> Imprimir </v-btn>
            <v-btn
              v-if="!invoiceDetails.paymentReceived && !isShipper"
              color="primary"
              class="mx-2 px-5"
              large
              @click="openInvoiceDelete"
            >
              Desfazer
            </v-btn>
          </v-card-actions>
        </v-card-text>
        <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import maskData from '@/utils/maskData';
import maskPrice from '@/utils/maskPrice';
import TruncateDataWithTooltip from '@/components/Table/TruncateDataWithTooltip.vue';
import MessagesError from '@/components/MessagesError.vue';

export default {
  props: {
    isOpen: {
      default: false,
      type: Boolean
    },
    invoiceId: null
  },

  components: {
    TruncateDataWithTooltip,
    MessagesError
  },

  data: () => ({
    isLoading: false,
    menu: false,
    activePicker: null,
    columnsCtes: [
      {
        value: 'boardingNumber',
        text: 'EMBARQUES EXTERNOS'
      },
      {
        value: 'number',
        text: 'NÚMEROS DE CTES'
      },
      {
        value: 'price',
        text: 'VALOR TOTAL'
      },
      {
        value: 'observation',
        text: 'OBSERVAÇÃO'
      }
    ],
    columnsAditionals: [
      {
        value: 'additionalDate',
        text: 'DATA DE CRIAÇÃO'
      },
      {
        value: 'isDiscount',
        text: 'TIPO'
      },
      {
        value: 'price',
        text: 'VALOR'
      },
      {
        value: 'complementaryAccessKeyCte',
        text: 'CTE COMPLEMENTAR'
      },
      {
        value: 'justification',
        text: 'JUSTIFICATIVA'
      },
      {
        value: 'observation',
        text: 'OBSERVAÇÃO'
      }
    ],
    paginationCtes: {
      pageNumber: 1,
      pageSize: 5,
      totalPages: 0
    },
    invoiceDetails: {
      id: null,
      numberInvoiceId: null,
      externalInvoiceNumber: null,
      invoiceDate: null,
      receiptForecast: null,
      paymentReceived: null,
      senderName: null,
      observation: null,
      approvalObservation: null,
      receiptNumber: null,
      descriptionCtes: [],
      descriptionAdditionals: []
    },
    errors: [],
    errorsType: 'error',
    error500: null
  }),

  computed: {
    ...mapGetters('LoginStore', ['getRole']),

    receiptDiscount() {
      const value = this.invoiceDetails.descriptionAdditionals
        .map((item) => {
          return item.isDiscount ? item.price : 0;
        })
        .reduce((total, price) => (total += price), 0);
      return value;
    },

    receiptValue() {
      const value1 = this.invoiceDetails.descriptionCtes
        .map((item) => item.price)
        .reduce((total, price) => (total += price), 0);

      const value2 = this.invoiceDetails.descriptionAdditionals
        .map((item) => {
          return !item.isDiscount ? item.price : 0;
        })
        .reduce((total, price) => (total += price), 0);

      return value1 + value2;
    },

    totalPagesTable() {
      const totalPages = Math.ceil(this.invoiceDetails.descriptionCtes.length / this.paginationCtes.pageSize);
      return totalPages;
    },

    aditionalsTableHeight() {
      const hasMoreThanFiveAditional = this.invoiceDetails.descriptionAdditionals.length > 10;
      return hasMoreThanFiveAditional ? '450' : '';
    },

    isShipper() {
      const role = this.getRole;
      return role == 'Embarcador';
    }
  },

  watch: {
    isOpen: {
      async handler(isOpen) {
        if (isOpen) {
          this.isLoading = true;

          const invoiceDetail = await this.getInvoiceDetail(this.invoiceId);
          this.invoiceDetails = invoiceDetail;

          this.isLoading = false;
        }
      }
    }
  },

  methods: {
    ...mapActions('InvoicesReceivableStore', ['fetchInvoicesDetails', 'exportPdfInvoice']),

    async getInvoiceDetail(invoiceId) {
      try {
        const invoiceDetail = await this.fetchInvoicesDetails(invoiceId);
        return invoiceDetail;
      } catch (e) {
        console.error(e);
      }
    },

    openInvoiceEdit() {
      this.close();
      this.$emit('edit-invoice', this.invoiceDetails);
    },

    openInvoiceDelete() {
      this.close();
      this.$emit('delete-invoice', this.invoiceDetails.id);
    },

    async getPDF() {
      try {
        const data = await this.exportPdfInvoice(this.invoiceDetails.id);
        if (data.success) {
          this.errors = ['Pdf exportado com sucesso!'];
          this.errorsType = 'success';
        } else {
          this.errors = data.errors || ['Erro ao imprimir fatura'];
          this.errorsType = 'error';
        }
      } catch (e) {
        this.errors = e;
        this.errorsType = 'error';
      } finally {
        this.setClearErrorTimer();
      }
    },

    onPageChange(page) {
      this.paginationCtes.pageNumber = page;
    },

    changeItemsPerPage(items) {
      this.paginationCtes.pageSize = items;
    },

    transformDate(date) {
      return maskData(date);
    },

    transformPrice(price) {
      return maskPrice(price);
    },

    transformDiscount(discount) {
      return discount ? 'Débito' : 'Crédito';
    },

    setClearErrorTimer() {
      setTimeout(() => {
        this.errors = [];
      }, 9000);
    },

    close() {
      this.$emit('update:isOpen', false);
      this.reset();
    },

    reset() {
      this.paginationCtes = {
        pageNumber: 1,
        pageSize: 5
      };

      this.invoice = {
        id: null,
        invoiceReceiptNumber: null,
        payDay: null,
        observation: null
      };
    }
  }
};
</script>
<style lang="scss" scoped>
.invoice-info {
  p {
    color: $--color-primary;
  }
}

.money-cards {
  padding: 10px 28px;
  border-radius: 5px;
  background: #1f77b4;
  color: white;
  span {
    font-size: 0.8rem;
    strong {
      font-size: 1.3rem;
      font-weight: 600;
    }
  }
  .circle {
    background: #1f77b4;
    border: solid 1px white;
    border-radius: 100%;
    padding: 0px;
  }
}

::v-deep .v-data-table-header tr th {
  background-color: #e0e0e0 !important;
  border: 1px solid #e0e0e0;
}

::v-deep .v-pagination.v-pagination {
  padding-left: 0;
  justify-content: flex-start;
  margin-left: 0;
  position: relative;
  bottom: 60px;
}
</style>
